import "./pricing-table.css";
import { HomeUpdates } from "./home-updates";
import { HomeTeacherVoices } from "./home-teacher-voices";
import { HomeArticles } from "./article-cards";
import { MARKET_CONTENT, isMarketLocale } from "../../src/i18n/market-content";
import React, { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { lastLoginMethod, markGoogleLoginPending, rememberLastLogin } from "./last-login";
import { type PetMode } from "../../src/pet-modes";
import { ToolCarousel } from "./featured-tools";
import { marketingPagesFor } from "../../src/marketing-pages";
import { HomeStats } from "./home-stats";
import { petGrowthScale } from "./pet-growth";
import { HomePetCardWall } from "./home-pet-card-wall";
import { PrivacyContent, TermsContent } from "./legal-content";
import { FX_DATE, FX_RATES, LOCALE_CURRENCY, currencyForTimezone, formatLocalPrice } from "./pricing-fx";
import { ContactForm, LINE_URL } from "./contact";
import { isDefaultLocale, formatDate, formatNumber, formatPrice, intlLocale, levelLabel, locale, lp, t, tx, type Locale } from "./i18n";
import { BrandMark, ChunkErrorBoundary, ForestIcon, type ForestIconName, type HistoryItem, LanguageSelector, PetEvolutionArt, PublicFooter, PublicHeader, type Session, type Snapshot, type TourRoutineDemo, api, homeToolHighlights, loadTourSession, petAsset, publicHref, teacherHref } from "./app-core";

// Prices are USD everywhere; the Chinese site spells the currency out (US$), English uses "$".
// D-090 plan prices and limits (src/plan-policy.ts, migration 0038); JSON-LD in src/i18n/page-metadata.ts must match.
const PRICE = { monthly: 3, annual: 19, firstYear: 9.9, plusMonthly: 5, plusAnnual: 29, plusFirstYear: 14.9 } as const;
const FREE = { classes: 1, students: 40, species: 12 } as const;
const TEACHER = { classes: 5, students: 100, species: 40 } as const;
const PLUS = { classes: 10, students: 100 } as const;
const currencySymbol = formatPrice(5).replace(/[\d\s.,\u00a0]/g, "");
/** Amount without the currency; cents only when needed (« 9,90 » in French and Spanish). */
const amountText = (amount: number) => formatNumber(amount, { minimumFractionDigits: Number.isInteger(amount) ? 0 : 2, maximumFractionDigits: 2 });
/** Big price figure: French writes the symbol after the amount (« 5 $US »). */
const PriceFigure = ({ amount }: { amount: number }) => (locale === "fr" || locale === "vi")
  ? <><strong>{amountText(amount)}</strong><sup>{`\u00a0${currencySymbol}`}</sup></>
  : <><sup>{currencySymbol}</sup><strong>{amountText(amount)}</strong></>;
const LockupFigure = ({ amount }: { amount: number }) => (locale === "fr" || locale === "vi")
  ? <><strong>{amountText(amount)}</strong><span className="price-currency">{`\u00a0${currencySymbol}`}</span></>
  : <><span className="price-currency">{currencySymbol}</span><strong>{amountText(amount)}</strong></>;
/** Schools and several teachers sharing: a quote through the contact form (topic 學校詢價). */
const schoolQuoteHref = () => `${lp("/contact")}?topic=school`;
const priceVars = () => ({ monthly: formatPrice(PRICE.monthly), annual: formatPrice(PRICE.annual), firstYear: formatPrice(PRICE.firstYear) });
const plusVars = () => ({ plusMonthly: formatPrice(PRICE.plusMonthly), plusAnnual: formatPrice(PRICE.plusAnnual), plusFirstYear: formatPrice(PRICE.plusFirstYear), classes: PLUS.classes, students: PLUS.students });

// Home-page product shots of the real demo classroom, captured per language
// (design/en-screenshots, design/es-screenshots, design/fr-screenshots). Names are relative to MARKETING_DIR,
// without ".webp"; the projection shots also ship an "-800" variant for srcset.
// A language without its own captures falls back to the English ones.
const MARKETING_DIR = "/assets/forest-ui/marketing/";
type HomeScreens = { projectionIndividual: string; projectionShared: string; dashboardIndividual: string; dashboardShared: string };
const HOME_SCREENS: Partial<Record<Locale, HomeScreens>> & { en: HomeScreens } = {
  "pt-BR": { projectionIndividual: "projection-individual-demo-pt-br-v1", projectionShared: "projection-shared-demo-pt-br-v1", dashboardIndividual: "dashboard-individual-demo-pt-br-v1", dashboardShared: "dashboard-shared-demo-pt-br-v1" },
  "tr": { projectionIndividual: "projection-individual-demo-tr-v1", projectionShared: "projection-shared-demo-tr-v1", dashboardIndividual: "dashboard-individual-demo-tr-v1", dashboardShared: "dashboard-shared-demo-tr-v1" },
  "vi": { projectionIndividual: "projection-individual-demo-vi-v1", projectionShared: "projection-shared-demo-vi-v1", dashboardIndividual: "dashboard-individual-demo-vi-v1", dashboardShared: "dashboard-shared-demo-vi-v1" },
  "id": { projectionIndividual: "projection-individual-demo-id-v1", projectionShared: "projection-shared-demo-id-v1", dashboardIndividual: "dashboard-individual-demo-id-v1", dashboardShared: "dashboard-shared-demo-id-v1" },

  "zh-Hans": { projectionIndividual: "projection-individual-demo-zh-hans-v1", projectionShared: "projection-shared-demo-zh-hans-v1", dashboardIndividual: "dashboard-individual-demo-zh-hans-v1", dashboardShared: "dashboard-shared-demo-zh-hans-v1" },
  "zh-Hant": { projectionIndividual: "projection-individual-demo-v1", projectionShared: "projection-shared-demo-v1", dashboardIndividual: "dashboard-individual-demo-v2", dashboardShared: "dashboard-shared-demo-v3" },
  en: { projectionIndividual: "projection-individual-demo-en-v1", projectionShared: "projection-shared-demo-en-v1", dashboardIndividual: "dashboard-individual-demo-en-v1", dashboardShared: "dashboard-shared-demo-en-v1" },
  es: { projectionIndividual: "projection-individual-demo-es-v1", projectionShared: "projection-shared-demo-es-v1", dashboardIndividual: "dashboard-individual-demo-es-v1", dashboardShared: "dashboard-shared-demo-es-v1" },
  fr: { projectionIndividual: "projection-individual-demo-fr-v1", projectionShared: "projection-shared-demo-fr-v1", dashboardIndividual: "dashboard-individual-demo-fr-v1", dashboardShared: "dashboard-shared-demo-fr-v1" },
};
const homeScreens = HOME_SCREENS[locale] ?? HOME_SCREENS.en;
const screenSrc = (name: string) => `${MARKETING_DIR}${name}.webp`;
const screenSrcSet = (name: string) => `${MARKETING_DIR}${name}-800.webp 800w, ${MARKETING_DIR}${name}.webp 1600w`;
const HERO_SCENES: Partial<Record<Locale, string>> & { en: string } = {
  "pt-BR": "projection-individual-demo-pt-br-v1",
  "tr": "projection-individual-demo-tr-v1",
  "vi": "projection-individual-demo-vi-v1",
  "id": "projection-individual-demo-id-v1",

  "zh-Hans": "hero-classroom-scene-zh-v1",
  "zh-Hant": "hero-classroom-scene-zh-v1",
  en: "hero-classroom-scene-en-v1",
  es: "hero-classroom-scene-es-v1",
  fr: "hero-classroom-scene-fr-v1",
};
const heroScene = HERO_SCENES[locale] ?? HERO_SCENES.en;
const SCENE_LOCALE: Record<Locale, string> = { "pt-BR": "en", tr: "en", vi: "en", id: "en", it: "en", th: "en", uk: "en", nl: "en",
  "zh-Hans": "zh", "zh-Hant": "zh", en: "en", es: "es", fr: "fr" };
const sceneStem = (scene: "pricing" | "about" | "tour") => `scene-${scene}-${SCENE_LOCALE[locale] ?? "en"}-v1`;
const SceneFigure = ({ scene, alt, className = "", eager = false }: { scene: "pricing" | "about" | "tour"; alt: string; className?: string; eager?: boolean }) => {
  const stem = sceneStem(scene);
  return <figure className={`marketing-scene ${className}`}><img src={screenSrc(stem)} srcSet={screenSrcSet(stem)} sizes="(max-width: 680px) calc(100vw - 32px), (max-width: 1100px) 48vw, 600px" width="1600" height="1001" loading={eager ? "eager" : "lazy"} fetchPriority={eager ? "high" : undefined} decoding="async" alt={alt} /></figure>;
};
const HERO_SCENE_ALT: Partial<Record<Locale, string>> & { en: string } = {
  "pt-BR": MARKET_CONTENT["pt-BR"].heroAlt,
  "tr": MARKET_CONTENT["tr"].heroAlt,
  "vi": MARKET_CONTENT["vi"].heroAlt,
  "id": MARKET_CONTENT["id"].heroAlt,

  en: "Children in a classroom look at the ClassMori demo class on an interactive chalkboard",
  es: "Niños en un aula miran la clase de demostración de ClassMori en una pizarra interactiva",
  fr: "Des enfants en classe regardent la classe de démonstration ClassMori sur un tableau interactif",
};

// The tour mounts the real teacher workspace, loaded on demand.
const InteractiveTour = React.lazy(() => import("./workspace").then((module) => ({ default: module.InteractiveTour })));

// The home-page live demo (classroom-stage + tour adapter): never part of the first paint.
const HomeLiveDemo = React.lazy(() => import("./home-live-demo"));
const loadHomeLiveDemo = () => Promise.all([
  api<TourData>(`/api/public-tour?mode=individual&lang=${locale}`),
  loadTourSession(),
  import("./home-live-demo"),
]).then(async ([data, , module]) => {
  const tour = { ...data, snapshot: { ...data.snapshot, access: { ...data.snapshot.access, plan: "teacher" } } } as TourData;
  await module.preloadHomeDemoArt(tour);
  return tour;
});

/**
 * Second screen of the home page: the real demo classroom, ready to score. A product shot holds
 * the space; the classroom loads once the page is idle or the visitor scrolls near it, so the
 * hero and its image never wait for it.
 */
function HomeLiveSection() {
  const [sectionRef, near] = useNearViewport<HTMLElement>(true, "700px");
  const [idle, setIdle] = useState(false);
  const [tour, setTour] = useState<TourData | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let handle = 0;
    const later = () => {
      const idleCallback = (window as Window & { requestIdleCallback?: (fn: () => void, options?: { timeout: number }) => number }).requestIdleCallback;
      handle = idleCallback ? idleCallback(() => setIdle(true), { timeout: 2500 }) : window.setTimeout(() => setIdle(true), 1200);
    };
    if (document.readyState === "complete") later(); else window.addEventListener("load", later, { once: true });
    return () => { window.removeEventListener("load", later); if (handle) { (window as Window & { cancelIdleCallback?: (id: number) => void }).cancelIdleCallback?.(handle); window.clearTimeout(handle); } };
  }, []);
  const start = idle || near;
  // While the demo is on screen, the phone LINE bubble steps aside (it would cover the reward dock).
  useEffect(() => {
    const frame = sectionRef.current?.querySelector(".home-live-frame");
    if (!frame || typeof IntersectionObserver === "undefined") return;
    const root = document.documentElement;
    const observer = new IntersectionObserver(([entry]) => root.classList.toggle("home-live-in-view", entry.isIntersecting));
    observer.observe(frame);
    return () => { observer.disconnect(); root.classList.remove("home-live-in-view"); };
  }, [sectionRef]);
  useEffect(() => {
    if (!start) return;
    let cancelled = false;
    loadHomeLiveDemo().then((data) => { if (!cancelled) setTour(data); }).catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [start]);
  const poster = <a className="home-live-poster" href={`${lp("/tour")}#tour-individual`}>
    <img src={screenSrc(homeScreens.dashboardIndividual)} width="1320" height="900" loading="lazy" decoding="async" alt={t("一人一寵模式的公開示範教室：上方舞台顯示選中學生的寵物與還差幾點換下一種模樣，下方是每個孩子的寵物名單")} />
    <span className="home-live-status" role="status">{failed ? t("示範教室暫時打不開，點這裡到完整導覽試玩") : t("正在打開示範教室…")}</span>
  </a>;
  return <section ref={sectionRef} className="home-live" id="live-classroom" data-home-module="M1-LIVE" aria-labelledby="home-live-title">
    <div className="section-heading"><p className="eyebrow">{t("不用註冊，現在就試")}</p><h2 id="home-live-title">{t("點一下，孩子的寵物就長大")}</h2><p>{t("這是公開示範班級，學生都是虛構代稱。操作只在這個頁面裡，重新整理就回到原樣。")}</p></div>
    <div className="home-live-frame">
      {tour ? <ChunkErrorBoundary><React.Suspense fallback={poster}><HomeLiveDemo tour={tour} /></React.Suspense></ChunkErrorBoundary> : poster}
    </div>
    <p className="home-live-more"><a href={`${lp("/tour")}#tour-individual`}>{t("看完整示範後台：課堂工具、兌換商店")} <span aria-hidden="true">→</span></a></p>
  </section>;
}

/** Phones only (CSS): the long home and tour pages keep a one-tap start button in reach once the hero has scrolled away. */
function StickyStartBar({ position }: { position: string }) {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const update = () => setShown(window.scrollY > 520);
    update();
    window.addEventListener("scroll", update, { passive: true });
    return () => window.removeEventListener("scroll", update);
  }, []);
  return <div className={`sticky-start${shown ? " is-visible" : ""}`}><span>{t("免費版永久可用")}</span><a className="button primary" href={teacherHref("/register")} data-cta-position={position}>{t("免費開始使用")}</a></div>;
}

export function Landing() {
  const startHref = teacherHref("/register");
  const heroSceneAlt = locale === "zh-Hant" || locale === "zh-Hans"
    ? t("示範教室的投影畫面：一人一寵模式，選中的孩子們帶著各自的寵物在森林舞台合影，下方是選學生記分的操作台")
    : HERO_SCENE_ALT[locale] ?? HERO_SCENE_ALT.en;
  return (
    <main className="landing marketing-site homepage-spec-v3">
      <PublicHeader className="" />
      <section className="hero marketing-hero" data-home-module="M1">
        {/* LCP image: phones get the 941px portrait art instead of the 1672px desktop one. */}
        <picture><source media="(max-width: 680px)" srcSet="/assets/forest-ui/cartoon-forest-mobile.webp" /><img className="marketing-hero-art" src="/assets/forest-ui/cartoon-forest-desktop.webp" width="1672" height="941" fetchPriority="high" alt={t("小森課堂的綠色森林與花草的手繪背景")} /></picture>
        <div className="hero-copy">
          <p className="hero-free-plan"><strong>{t("免費版永久可用")}</strong></p>
          <h1>{t("上課點一下加分，")}<br />{t("孩子的寵物當場長大")}</h1>
          <p>{t("每個孩子都有自己的寵物，全班在大螢幕上一起看見。")}</p>
          <div className="login-actions"><a className="button primary" href={startHref}>{t("免費開始使用")}</a><a className="button secondary" href="#live-classroom">{t("先玩示範教室（免註冊）")}</a></div>
          <p className="hero-trial-note">{t("不用信用卡，註冊再送 30 天標準版。")}</p>
        </div>
        {/* Locale-specific real demo projection is baked into this illustrative classroom photo. */}
        <figure className="hero-product-frame hero-scene"><img className="hero-scene-image" src={screenSrc(heroScene)} srcSet={isMarketLocale(locale) ? screenSrcSet(heroScene) : `${MARKETING_DIR}${heroScene}-900.webp 900w, ${screenSrc(heroScene)} 1800w`} sizes="(max-width: 980px) min(640px, calc(100vw - 40px)), min(660px, 44vw)" width="1800" height="1126" fetchPriority="high" alt={heroSceneAlt} /></figure>
      </section>
      <HomeStats />
      <HomeLiveSection />
      <section className="a1r home-contrast" id="why-classmori" data-home-module="M2" aria-labelledby="home-contrast-title">
        <div className="a1r-head"><span className="a1r-tag">{t("記分方式比較")}</span><h2 id="home-contrast-title">{t("下課了，還在整理便利貼？")}</h2></div>
        <div className="a1r-columns">
          <div className="a1r-side a1r-before"><span className="a1r-kicker">{t("紙本便利貼")}</span><div className="a1r-note-area"><div className="a1r-note-back" aria-hidden="true" /><div className="a1r-note"><h3><span>{t("課後整理散落的紙")}</span><span>{t("一張張核對抄謄")}</span></h3><ul className="a1r-scribbles"><li>{t("語晴 主動發言 +2")}</li><li>{t("小安 協助同學 +1")}</li><li>{t("⋯還有幾張待確認")}</li></ul></div><img className="a1r-tired" src="/assets/forest-ui/marketing/otter-comparison-tired-v1-480.webp" width="480" height="525" loading="lazy" decoding="async" alt={t("小森水獺抱著待整理紙張，露出疲倦的表情")} /></div><p className="a1r-side-foot">{t("整理完已是放學後")}</p></div>
          <div className="a1r-side a1r-after"><span className="a1r-kicker">{t("班級寵物積分")}</span><h3><span>{t("選對象、點加分原因")}</span><span>{t("紀錄當下即完成")}</span></h3><div className="a1r-monitor"><div className="a1r-bezel"><div className="a1r-screen"><div className="a1r-sidebar" aria-hidden="true"><i /><i /><i /><i /></div><div className="a1r-ui"><div className="a1r-ui-top"><span>{t("小森課堂")}</span><i aria-hidden="true" /></div><div className="a1r-ui-content"><div className="a1r-ui-row"><i aria-hidden="true">✓</i><span>{t("語晴｜主動發言 +2 ✓")}</span></div><div className="a1r-ui-row"><i aria-hidden="true">✓</i><span>{t("小安｜協助同學 +1 ✓")}</span></div><div className="a1r-ui-row"><i aria-hidden="true">✓</i><span>{t("今日明細隨時可查")}</span></div><div className="a1r-ui-progress" aria-hidden="true" /></div><img className="a1r-happy" src="/assets/forest-ui/marketing/otter-comparison-happy-v1-480.webp" width="480" height="525" loading="lazy" decoding="async" alt={t("小森水獺開心地舉起爪子")} /></div></div></div><div className="a1r-monitor-stand" aria-hidden="true" /><div className="a1r-monitor-base" aria-hidden="true" /></div></div>
        </div>
      </section>
      <section className="product-showcase" id="product" data-home-module="M3">
        <div className="showcase-copy"><p className="eyebrow">{t("上課這樣用")}</p><h2>{t("點一下，全班都看見")}</h2><p>{t("選學生、選原因，就記好。")}</p><p>{t("紀錄自動保存，點錯可撤銷。")}</p><a className="home-feature-more" href={lp("/features/quick-scoring")}>{t("了解快速記分與投影")} <span aria-hidden="true">→</span></a></div>
        <figure className="showcase-art showcase-screen"><img src={screenSrc(homeScreens.projectionIndividual)} srcSet={screenSrcSet(homeScreens.projectionIndividual)} sizes="(max-width: 680px) 94vw, 760px" width="1600" height="1000" alt={t("示範教室的投影畫面：一人一寵模式，選中的孩子們帶著各自的寵物在森林舞台合影，下方是選學生記分的操作台")} loading="lazy" decoding="async" /><figcaption>{t("示範教室實際投影畫面")}</figcaption></figure>
      </section>
      <section className="home-difference" data-home-module="M3-DIFF" aria-labelledby="home-difference-title">
        <div className="section-heading"><p className="eyebrow">{t("為什麼選小森課堂")}</p><h2 id="home-difference-title">{t("和常見的班級經營 App，")}<br />{t("有什麼不一樣？")}</h2></div>
        <div className="home-difference-grid">
          <article><ForestIcon name="students" /><h3>{t("學生、家長都不用帳號")}</h3><p>{t("老師建好名單就能開始。")}</p></article>
          <article><ForestIcon name="sprout" /><h3>{t("每個孩子一隻會長大的寵物")}</h3><p>{t("積分累積，寵物跟著長大。")}</p></article>
          <article><ForestIcon name="classroom" /><h3>{t("為台灣課堂而做")}</h3><p>{t("介面有繁體中文、英文、西班牙文與法文。免費版 1 個班一直能用；帶好幾個班，標準版一年 {annual}。", { annual: formatPrice(PRICE.annual) })}</p></article>
        </div>
      </section>
      {/* 2026-09 改版暂时隐藏：原因见 spec；classroom-scenes 原码备份于 backups/homepage-2026-09-v3/modules/，可撤销信息并入 M3。 */}
      <section className="pet-evolution" id="pet-growth" data-home-module="M4">
        <div className="section-heading"><p className="eyebrow">{t("成長路線")}</p><h2>{t("寵物跟著積分長大")}</h2></div>
        <div className="evolution-track">
          {([
            { stage: 0, title: t("小窩"), level: t("未出場") },
            { stage: 1, title: t("幼體"), level: t("Lv.{from}–{to}", { from: 1, to: 9 }) },
            { stage: 10, title: t("少年"), level: t("Lv.{from}–{to}", { from: 10, to: 19 }) },
            { stage: 20, title: t("青年"), level: t("Lv.{from}–{to}", { from: 20, to: 29 }) },
            { stage: 30, title: t("成熟體"), level: levelLabel(30) },
          ] as const).map(({stage,title,level}) => <article key={stage}><div className="evolution-art" style={{"--growth":petGrowthScale(stage, stage === 0)} as React.CSSProperties}><PetEvolutionArt species="otter" visualStage={stage} anchor="bottom" label={t("水獺個人寵物{stage}形態", { stage: title })} /></div><i aria-hidden="true" /><h3>{title}<small>{level}</small></h3></article>)}
        </div>
        <p className="evolution-art-note">{t("每種寵物都有自己的成長模樣。")}</p>
        <p className="evolution-art-note">{t("加分讓寵物成長；提醒扣分或撤銷加分可能讓寵物退階，商店兌換只扣「可兌換」點數，寵物不受影響。")}</p>
        <HomePetCardWall />
      </section>
      {/* D-275: teachers' own words in Chinese; other languages skip it (see DECISIONS). */}
      {locale === "zh-Hant" && <HomeTeacherVoices />}
      {/* 2026-09 改版暂时隐藏：原因见 spec；teamwork-story 原码备份于 backups/homepage-2026-09-v3/modules/，内容并入 M3、M4。 */}
      <div className="home-trial-moment" data-home-module="M4-CTA" data-trial-position="growth"><div><h3>{t("想看看你班上的孩子，會養出什麼樣的寵物？")}</h3></div><a className="button primary" href={startHref}>{t("免費開始使用")} <span aria-hidden="true">→</span></a></div>
      <section className="home-pricing" id="plans" data-home-module="M8">
        <div className="home-pricing-art"><img src="/assets/forest-ui/marketing/otter-gardener-v5.webp" width="583" height="633" alt={t("穿著園藝圍裙的小森捧著新芽")} loading="lazy" /></div>
        <div className="home-pricing-copy"><p className="eyebrow">{t("價格")}</p><h2>{t("免費版永久可用，")}<br />{t("需要更多再升級")}</h2>
          <div className="price-pair price-trio" role="list" aria-label={t("方案價格")}>
            <div className="price-pair-option" role="listitem"><span className="price-plan-label">{t("免費版")}</span><div className="price-lockup-amount"><LockupFigure amount={0} /><em>{t("永久")}</em></div><small>{t("{classes} 個班・每班 {students} 位學生", { classes: FREE.classes, students: FREE.students })}</small></div>
            <div className="price-pair-option best" role="listitem"><span className="price-plan-label">{t("標準版・首年")}</span><div className="price-lockup-amount"><LockupFigure amount={PRICE.firstYear} /><em>{t("／年")}</em></div><small>{t("原價")} <s>{t("{annual}／年", priceVars())}</s></small></div>
            <div className="price-pair-option" role="listitem"><span className="price-plan-label">{t("學校方案")}</span><div className="price-lockup-amount"><strong className="price-quote">{t("詢價")}</strong></div><small>{t("多位老師或全校一起用")}</small></div>
          </div>
          <div className="home-pricing-actions"><a className="button primary" href={startHref} data-trial-position="pricing">{t("免費開始使用")}</a><a className="home-pricing-details" href={lp("/pricing")}>{t("比較免費版與標準版")} <span aria-hidden="true">→</span></a></div></div>
      </section>
      <section className="home-tools-showcase home-free-tools" id="classroom-tools" data-home-module="M6" aria-labelledby="home-tools-title">
        <div className="home-tools-heading"><div><p className="eyebrow">{t("免費・免登入・免註冊")}</p><h2 id="home-tools-title">{t("每天的小事，也一起收好")}</h2></div></div>
        <ToolCarousel position="home_tools" />
        <div className="home-tools-links"><a className="button secondary" href={lp("/tools")} data-cta-position="home_tools_all">{t("看全部 {count} 個免費工具", { count: marketingPagesFor(locale).tools.length })} <span aria-hidden="true">→</span></a></div>
      </section>
      <HomeUpdates />
      <HomeArticles />
      {/* 2026-09 改版暂时隐藏：原因见 spec；home-routine-showcase 原码备份于 backups/homepage-2026-09-v3/modules/，改为 M6 例行板卡片。 */}
      {/* 2026-09 改版暂时隐藏：原因见 spec；feature-bento 原码备份于 backups/homepage-2026-09-v3/modules/，日常功能并入 M6。 */}
      <section className="privacy-feature" id="privacy" data-home-module="M7">
        <img src="/assets/forest-ui/marketing/privacy-garden-v4.webp" width="1536" height="1024" alt={t("老師與孩子在受葉片守護的森林花園中學習")} loading="lazy" />
        <div><p className="eyebrow">{t("老師最常問的")}</p><h2>{t("不增加孩子的負擔，也不增加你的")}</h2><div className="home-privacy-points">
          <article><ForestIcon name="shield" /><div><h3>{t("① 學生不用註冊")}</h3><p>{t("不用帳號、電子郵件、電話或照片。老師建好名單就能開始。")}</p></div></article>
          <article><ForestIcon name="heart" /><div><h3>{t("② 投影、平板、手機都能用")}</h3></div></article>
          <article><ForestIcon name="teacher" /><div><h3>{t("③ 老師說了算")}</h3><p>{t("用哪種養法、要不要顯示誰貢獻最多，都由你決定。")}</p></div></article>
          <article><ForestIcon name="cooperate" /><div><h3>{t("④ 資料只留在你的工作空間")}</h3><p>{t("需要取回或刪除資料，來信申請，我們協助處理。")}</p></div></article>
        </div><a href={lp("/privacy")}>{t("閱讀隱私政策")} <span aria-hidden="true">→</span></a></div>
      </section>
      {/* 2026-09 改版暂时隐藏：原因见 spec；brand-principles 原码备份于 backups/homepage-2026-09-v3/modules/，并入 M7 和 FAQ。 */}
      <section className="marketing-faq" data-home-module="M9">
        <div className="section-heading"><h2>{t("開始前，你可能想知道")}</h2></div>
        <div className="faq-grid">
          <details><summary>{t("孩子會不會只在乎分數？")}</summary><p>{t("每次加分都帶著具體原因，孩子知道自己是因為「交了作業」「幫了同學」被肯定，而不只是拿到一個數字。要不要公開誰貢獻最多，也由老師決定。")}</p></details>
          <details><summary>{t("加分的原因可以自己設定嗎？也能扣分嗎？")}</summary><p>{t("可以。加分和提醒扣分的原因、分數都由老師自己設定，要不要用扣分也由你決定。積分預設不會扣成負數，也可以為班級開啟「允許負分」。")}</p></details>
          <details><summary>{t("積分可以換東西嗎？")}</summary><p>{t("可以。老師在兌換商店上架自己準備的獎勵或小特權，孩子存夠積分，由老師在後台幫他兌換（標準版也能讓孩子從專屬連結送出申請，老師批准才扣點）；也可以把商品設成扭蛋或刮刮卡，從老師訂的獎品池隨機抽一項。兌換只扣「可兌換」點數，寵物等級不受影響；提醒扣分仍可能讓寵物退階，建議先跟孩子說好規則。")}</p></details>
          <details><summary>{t("學生需要註冊嗎？")}</summary><p>{t("不用。老師建立或匯入名單就能開始，孩子不需要帳號、電子郵件或電話。")}</p></details>
          <details><summary>{t("上課操作會不會很花時間？")}</summary><p>{t("選孩子、選原因，兩下就完成，也可以一次選一組或全班。點錯可以立即撤銷。")}</p></details>
          <details><summary>{t("寵物是每人一隻，還是全班一隻？")}</summary><p>{t("建班時選，預設推薦一人一隻；之後也能在班級設定隨時切換。兩種方式都分開記錄每個孩子的積分，切換不會改變點數。")}</p></details>
          <details><summary>{t("能在手機上用嗎？")}</summary><p>{t("可以。手機版和電腦版的選單一致，底部導航方便單手操作；上課也可以直接投影。")}</p></details>
          <details><summary>{t("免費版真的一直免費嗎？")}</summary><p>{t("是。免費版沒有使用期限：1 個班、每班最多 40 位學生，記分、兩種養寵模式與完整成長、投影、課堂紀錄、兌換商店、例行板和常用課堂工具都能用，不需要信用卡。")}</p></details>
          <details><summary>{t("30 天試用結束後會怎樣？")}</summary><p>{t("註冊送的是 30 天標準版試用。結束後自動轉為免費版，不扣款，也不刪資料；想繼續用標準版功能，再自己決定是否購買。")}</p></details>
          <details><summary>{t("學校可以統一採購嗎？")}</summary><p>{t("可以。學校或多位老師一起用目前採個別詢價，請到聯絡頁選「學校詢價」，或來信 support@classmori.com。")}</p></details>
        </div>
      </section>
      <section className="marketing-final" data-home-module="M10"><BrandMark /><h2>{t("下一堂課就能用上")}</h2><p>{t("免費開始，不需信用卡。")}</p><div><a className="button primary" href={startHref}>{t("免費開始使用")}</a><a className="button secondary" href={lp("/tour")}>{t("先玩示範教室")}</a></div></section>
      <PublicFooter />
      <StickyStartBar position="home_sticky" />
    </main>
  );
}

/** Current address without the one-time token (keeps ?lang= and the rest). */
function withoutToken(path: string) {
  const params = new URLSearchParams(location.search);
  params.delete("token");
  return params.size ? `${path}?${params}` : path;
}

type AuthMode = "login" | "register" | "verify-email" | "forgot-password" | "reset-password";

/** /login and /register are one page (D-106): Google first, email on request; a new address signs up. */
export function EmailAuthPage({ mode, next }: { mode: AuthMode; next?: string }) {
  return mode === "login" || mode === "register" ? <SignInPage next={next} /> : <AccountEmailPage mode={mode} next={next} />;
}

function GoogleMark() {
  return <svg className="auth-google-mark" viewBox="0 0 18 18" aria-hidden="true" focusable="false">
    <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z" />
    <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z" />
    <path fill="#FBBC05" d="M3.97 10.72A5.4 5.4 0 0 1 3.68 9c0-.6.1-1.18.29-1.72V4.95H.96A9 9 0 0 0 0 9c0 1.45.35 2.83.96 4.05l3.01-2.33z" />
    <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z" />
  </svg>;
}

/** Same-origin path to return to after sign-in, or "" (rejects //host, /\\host and other origins). */
function safeReturnPath(candidate: string) {
  if (!candidate.startsWith("/") || candidate.includes("\\")) return "";
  try {
    const url = new URL(candidate, location.origin);
    return url.origin === location.origin ? `${url.pathname}${url.search}${url.hash}` : "";
  } catch { return ""; }
}

function SignInPage({ next }: { next?: string }) {
  const returnTo = safeReturnPath(next ?? new URLSearchParams(location.search).get("next") ?? "");
  // Hong Kong sign-up links carry ?market=HK (Traditional Chinese only).
  const market = new URLSearchParams(location.search).get("market") === "HK" ? "HK" : "TW";
  const [config, setConfig] = useState<{ email: boolean; google: boolean } | null>(null);
  const [configFailed, setConfigFailed] = useState(false);
  const lastMethod = useMemo(() => lastLoginMethod(), []);
  const [emailOpen, setEmailOpen] = useState(lastMethod === "email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [sentTo, setSentTo] = useState("");
  const [resendIn, setResendIn] = useState(0);
  const [message, setMessage] = useState(() => {
    const oauth = new URLSearchParams(location.search).get("oauth");
    return oauth === "cancelled" ? t("已取消 Google 登入，可重新選擇帳號或使用電子郵件登入。") :
      oauth === "failed" ? t("Google 登入未完成，請重試或使用電子郵件登入。") :
      oauth === "unverified" ? t("Google 帳號的電子郵件尚未驗證，請先完成驗證。") : "";
  });
  useEffect(() => {
    void api<{ emailRegistrationEnabled: boolean; googleLoginEnabled: boolean }>("/api/public-config")
      .then((result) => { setConfig({ email: result.emailRegistrationEnabled, google: result.googleLoginEnabled }); if (!result.googleLoginEnabled) setEmailOpen(true); })
      // Without the config, still offer email sign-in (existing accounts work regardless).
      .catch(() => { setConfigFailed(true); setEmailOpen(true); });
  }, []);
  useEffect(() => {
    if (resendIn <= 0) return;
    const timer = window.setTimeout(() => setResendIn((seconds) => seconds - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [resendIn]);
  async function continueWithEmail(resend = false) {
    setBusy(true);
    setMessage("");
    try {
      const result = await api<{ status: "signed_in" | "verify_sent"; redirect?: string }>("/auth/email", { method: "POST", body: JSON.stringify({ email: resend ? sentTo : email, password, locale, market }) });
      rememberLastLogin("email");
      if (result.status === "signed_in") { location.assign(returnTo || result.redirect || "/"); return; }
      if (resend) setMessage(t("已重寄驗證信，請再看看信箱（含垃圾信件匣）。"));
      setSentTo(resend ? sentTo : email);
      setResendIn(60);
    } catch (error) { setMessage((error as Error).message); }
    finally { setBusy(false); }
  }
  function submit(event: FormEvent) {
    event.preventDefault();
    void continueWithEmail();
  }
  const googleHref = `/auth/google?next=${encodeURIComponent(returnTo || "/")}`;
  return <main className="auth-page">
    <a className="brand" href={publicHref("/")}><BrandMark />{t("小森課堂")}</a>
    <section className="auth-card auth-sign-in">
      <h1>{t("登入小森課堂")}</h1>
      <p className="auth-sign-in-lead">{t("第一次使用會自動建立帳號：免費版永久可用，再送 30 天標準版試用，不需信用卡。")}</p>
      {lastMethod && !sentTo && <p className="auth-last-login" role="note">{lastMethod === "email" ? t("這個瀏覽器上次是用電子郵件登入。之前用電子郵件註冊的老師，請繼續用同一組登入；換成 Google 會是另一個帳號。") : t("這個瀏覽器上次是用 Google 登入。")}</p>}
      {sentTo ? <div className="auth-check-inbox" role="status">
        <h2>{t("請到信箱收信")}</h2>
        <p>{t("驗證信已寄到 {email}。請在 30 分鐘內開啟信中的連結；在這台裝置上開啟就不必再輸入密碼。", { email: sentTo })}</p>
        <p className="auth-check-inbox-hint">{t("沒收到？看看垃圾信件匣或「促銷內容」分頁，或等一分鐘後重寄。")}</p>
        <div className="auth-check-inbox-actions">
          <button type="button" className="button secondary" disabled={busy || resendIn > 0} onClick={() => void continueWithEmail(true)}>{resendIn > 0 ? t("{seconds} 秒後可重寄", { seconds: resendIn }) : t("重寄驗證信")}</button>
          <button type="button" className="text-button" onClick={() => { setSentTo(""); setMessage(""); }}>{t("改用其他電子郵件")}</button>
        </div>
        {config?.google && <p className="auth-check-inbox-hint">{t("等不到信？也可以直接")} <a href={googleHref} onClick={markGoogleLoginPending}>{t("使用 Google 繼續")}</a></p>}
        {message && <p className="notice">{message}</p>}
      </div> : <>
        <div className="auth-sign-in-choices">
          {config?.google && <a className="button primary auth-choice" href={googleHref} onClick={markGoogleLoginPending}><GoogleMark />{t("使用 Google 繼續")}</a>}
          {config?.google && !emailOpen && <button type="button" className="button secondary auth-choice" onClick={() => setEmailOpen(true)}>{t("使用電子郵件繼續")}</button>}
        </div>
        {emailOpen && <form className="form-stack auth-email-form" onSubmit={submit}>
          {config?.google && <p className="auth-divider"><span>{t("或使用電子郵件")}</span></p>}
          <label>{t("電子郵件")}
            <input type="email" required autoComplete="email" autoFocus={Boolean(config?.google)} value={email} onChange={(event) => setEmail(event.target.value)} />
          </label>
          <label>{t("密碼")}
            <input type="password" required autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} />
          </label>
          <p className="auth-field-hint">{t("第一次使用：設定至少 12 字的密碼，送出後到信箱點驗證連結即可開始。")}</p>
          <button className="button primary" disabled={busy}>{busy ? t("處理中…") : t("繼續")}</button>
        </form>}
        {configFailed && <p className="notice" role="status">{t("暫時無法確認登入方式，請檢查網路後")}<button type="button" className="text-button" onClick={() => location.reload()}>{t("重新整理")}</button>{isDefaultLocale ? "。" : "."}</p>}
        {message && <div className="notice" role="status">{message}</div>}
      </>}
      <div className="auth-links">
        <a href={teacherHref("/forgot-password")}>{t("忘記密碼？")}</a>
      </div>
      {/* D-280: one language picker (as in the site footer), not every language spelled out under the card. */}
      <div className="auth-language"><LanguageSelector /></div>
    </section>
  </main>;
}

function AccountEmailPage({ mode }: { mode: Exclude<AuthMode, "login" | "register">; next?: string }) {
  // The token leaves the address bar (below) but stays in this tab, so switching the
  // page language (a reload) does not lose the link.
  const tokenKey = `classmori-${mode}-token`;
  const initialToken = (() => {
    const fromUrl = new URLSearchParams(location.search).get("token");
    try {
      if (fromUrl) sessionStorage.setItem(tokenKey, fromUrl);
      return fromUrl ?? ((mode === "verify-email" || mode === "reset-password") ? sessionStorage.getItem(tokenKey) ?? "" : "");
    } catch { return fromUrl ?? ""; }
  })();
  const [token] = useState(initialToken);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  // Verification: the registering browser verifies in one step; others are asked for the password.
  const [needsPassword, setNeedsPassword] = useState(mode !== "verify-email");
  // An invalid or expired link cannot be rescued with the password; show only the error.
  const [verifyFailed, setVerifyFailed] = useState(false);
  const verified = useRef(false);
  useEffect(() => {
    if (mode !== "verify-email" || verified.current) return;
    verified.current = true;
    history.replaceState(null, "", withoutToken("/verify-email"));
    if (!token) { setMessage(t("驗證連結缺少憑證，請重新註冊。")); setVerifyFailed(true); return; }
    // Same browser as the registration: no password needed (the server checks a cookie).
    void api<{ redirect: string }>("/auth/verify-email", { method: "POST", body: JSON.stringify({ token, locale }) })
      .then((result) => location.assign(result.redirect))
      .catch((error: Error & { code?: string }) => {
        if (error.code === "verification_password_required") setNeedsPassword(true);
        else { setMessage(error.message); setVerifyFailed(true); }
      });
  }, [mode, token]);
  useEffect(() => {
    if (mode === "reset-password") history.replaceState(null, "", withoutToken("/reset-password"));
  }, [mode]);
  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      if (mode === "verify-email") {
        // The link alone is not enough: the registration password proves it is the same person.
        const result = await api<{ redirect: string }>("/auth/verify-email", { method: "POST", body: JSON.stringify({ token, password }) });
        location.assign(result.redirect);
      } else if (mode === "forgot-password") {
        const result = await api<{ message: string }>("/auth/password-reset/request", { method: "POST", body: JSON.stringify({ email, locale }) });
        setMessage(result.message);
      } else {
        await api("/auth/password-reset/confirm", { method: "POST", body: JSON.stringify({ token, password }) });
        setMessage(t("密碼已更新，請使用新密碼登入。"));
      }
    } catch (error) { setMessage((error as Error).message); }
    finally { setBusy(false); }
  }
  const title = { "verify-email": t("驗證電子郵件"), "forgot-password": t("忘記密碼"), "reset-password": t("設定新密碼") }[mode];
  return <main className="auth-page">
    <a className="brand" href={publicHref("/")}><BrandMark />{t("小森課堂")}</a>
    <section className="auth-card">
      <p className="eyebrow">{t("老師帳號")}</p>
      <h1>{title}</h1>
      {mode === "verify-email" && !needsPassword && !verifyFailed && <p role="status">{t("正在驗證你的電子郵件…")}</p>}
      {mode === "verify-email" && verifyFailed && <div className="notice" role="alert">{message}</div>}
      {mode === "verify-email" && needsPassword && <p>{t("這個連結是在另一個瀏覽器或裝置開啟的。為了確認是你本人註冊，請輸入註冊時設定的密碼，完成驗證後就能開始建立班級。")}</p>}
      {!(mode === "verify-email" && !needsPassword) && <form className="form-stack" onSubmit={submit}>
          {mode === "forgot-password" && <label>{t("電子郵件")}
            <input type="email" required autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} />
          </label>}
          {mode !== "forgot-password" && <label>{mode === "verify-email" ? t("註冊時設定的密碼") : t("密碼")}
            <input type="password" required minLength={mode === "verify-email" ? undefined : 12} autoComplete={mode === "verify-email" ? "current-password" : "new-password"} value={password} onChange={(event) => setPassword(event.target.value)} />
          </label>}
          {mode === "forgot-password" && <p>{t("只適用以電子郵件設定密碼的帳號；用 Google 登入的老師請直接按「使用 Google 繼續」。")}</p>}
          {mode === "reset-password" && !token && <p className="notice">{t("重設連結缺少憑證，請重新申請。")}</p>}
          <button className="button primary" disabled={busy || ((mode === "reset-password" || mode === "verify-email") && !token)}>{busy ? t("處理中…") : mode === "verify-email" ? t("完成驗證") : title}</button>
          {message && !verifyFailed && <div className="notice" role="status">{message}</div>}
        </form>}
      <div className="auth-links">
        <a href={teacherHref("/login")}>{t("返回登入")}</a>
      </div>
      {/* D-280: one language picker (as in the site footer), not every language spelled out under the card. */}
      <div className="auth-language"><LanguageSelector /></div>
    </section>
  </main>;
}

export function EmptyWorkspaceState({ session }: { session: Session }) {
  return <main className="auth-page">
    <a className="brand" href={publicHref("/")}><BrandMark />{t("小森課堂")}</a>
    <section className="auth-card">
      <p className="eyebrow">{t("工作空間狀態")}</p>
      <h1>{session.access?.state === "suspended" ? t("工作空間暫停使用") : t("目前沒有使用中的班級")}</h1>
      <p>{t("{name}，你的帳號與工作空間仍然保留；目前尚未建立班級，因此沒有課堂資料可查看。", { name: session.teacher.displayName })}</p>
      <p>{t("班級資料與紀錄都會保留。免費版可使用 1 個班級、每班 40 位學生；需要更多班級，可以升級標準版。")}</p>
      <a className="button primary" href="/workspace/billing">{t("查看方案")}</a>      <button className="button secondary" onClick={async () => {
        await api("/auth/logout", { method: "POST", body: "{}" });
        location.assign("/");
      }}>{t("登出")}</button>
    </section>
  </main>;
}

export function AboutPage() {
  return <main className="about-page public-page">
    <PublicHeader />
    <section className="about-hero">
      <div className="about-hero-copy">
        <p className="eyebrow">{t("關於我們 · 一個小小的開始")}</p>
        <h1>{t("把課堂上的具體肯定記下來")}</h1>
        <p>{t("小森課堂是 3 個人做的班級經營工具。老師選學生、點原因，就記好一筆具體肯定；寵物隨積分成長，紀錄自動留下。")}</p>
        <div className="about-actions"><a className="button primary" href={teacherHref("/register")}>{t("免費開始使用")}</a><a className="button secondary" href="#our-story">{t("讀我們的故事")}</a></div>
      </div>
      <SceneFigure scene="about" className="about-hero-scene" alt={t("老師坐在學生旁邊，看學生在平板上點選小森課堂共養示範班")} eager />
    </section>
    <section className="about-origin" id="our-story">
      <div className="about-section-index"><span>01</span><p>{t("我們看見的事")}</p></div>
      <div><h2>{t("把一句「做得好」，說得更具體")}</h2><p>{t("設計時我們常想到這樣的片段：小組討論快結束時，一個孩子原本急著說答案，卻停下來，等同學把話說完。老師通常只有幾秒鐘回應，要記下的就是這件具體的事：「等同學說完」。")}</p><p>{t("所以記分只有兩步：選學生、選具體原因。寵物即時回應，紀錄同步留下，不用下課後再抄一次。")}</p><p className="about-scene-note">{t("以上是用來說明設計思路的課堂情境，並非特定學校或學生的真實案例。")}</p></div>
    </section>
    <section className="about-vision">
      <div className="about-section-index"><span>02</span><p>{t("願景與初心")}</p></div>
      <div className="about-vision-copy"><h2>{t("每個孩子的加分都分開記錄")}</h2><p>{t("安靜的孩子做對一件小事，老師點一下就記下。肯定什麼由老師決定，小森課堂記住時間、原因和點數，下課不用再整理。")}</p></div>
      <div className="about-beliefs"><article><ForestIcon name="heart" /><h3>{t("記下具體原因")}</h3><p>{t("每次加分都附上原因，例如「交了作業」「幫了同學」。")}</p></article><article><ForestIcon name="cooperate" /><h3>{t("一起長大")}</h3><p>{t("小組一起加分、全班一起合照，每個孩子的夥伴一起長大。")}</p></article><article><ForestIcon name="shield" /><h3>{t("老師決定")}</h3><p>{t("加分原因、要不要扣分、要不要顯示誰貢獻最多，都由老師設定；孩子不用帳號。")}</p></article></div>
    </section>
    <section className="about-research" aria-labelledby="about-research-title">
      <div className="about-section-index"><span>03</span><p>{t("教育理念與研究依據")}</p></div>
      <div className="about-research-heading"><h2 id="about-research-title">{t("設計參考的研究")}</h2><p>{t("我們把教育研究當成設計時的提問方式：回饋是否指向具體行為？孩子能否感到自己正在進步？全班能否一起參與？以下研究啟發了這些選擇。")}</p></div>
      <div className="about-research-grid">
        <article><span className="about-research-number">128</span><p className="about-research-meta">{t("位五年級學生 · 研究 1")}</p><h3>{t("肯定努力，而非貼上「聰明」標籤")}</h3><p>{t("心理學家 Claudia Mueller 與 Carol Dweck 在 1998 年發表的實驗中，研究 1 納入 128 位五年級學生；整篇研究比較了稱讚智力與稱讚努力後，孩子面對學習和挫折的反應。我們因此讓老師選擇可描述的行為原因，而不是替孩子貼上能力標籤。")}</p><a href="https://www.columbia.edu/cu/psychology/courses/3615/Readings/Mueller_Dweck.pdf" target="_blank" rel="noopener noreferrer">{t("閱讀 Mueller 與 Dweck 原始研究 ↗")}</a></article>
        <article><span className="about-research-number">3</span><p className="about-research-meta">{t("項基本心理需求 · 自我決定理論")}</p><h3>{t("讓孩子感到有能力、有選擇，也有連結")}</h3><p>{t("心理學家 Richard Ryan 與 Edward Deci 提出的自我決定理論，重視自主感、勝任感與關係感三項心理需求。所以每個孩子能選擇並養成自己的夥伴，全班也一起成長；回饋內容與要不要顯示點數排行，由老師決定。")}</p><a href="https://selfdeterminationtheory.org/SDT/documents/2000_RyanDeci_SDT.pdf" target="_blank" rel="noopener noreferrer">{t("閱讀 Ryan 與 Deci 原始論文 ↗")}</a></article>
        <article><span className="about-research-number">3</span><p className="about-research-meta">{t("個回饋問題 · 回饋模型")}</p><h3>{t("知道目標、現在的位置與下一步")}</h3><p>{t("教育研究者 John Hattie 與 Helen Timperley 的回饋模型提出三個問題：「我要去哪裡？」「我進行得如何？」「下一步是什麼？」小森課堂 幫老師留下當下的具體肯定；課堂目標與下一步，仍由老師和孩子一起決定。")}</p><a href="https://open-publishing.org/wp-content/uploads/2024/02/Hattie-PowerFeedback-2007.pdf" target="_blank" rel="noopener noreferrer">{t("閱讀 Hattie 與 Timperley 原始論文 ↗")}</a></article>
      </div>
      <p className="about-research-note">{t("這些研究提供設計參考；上述參與人數是原研究樣本，不是小森課堂的使用人數或產品成效數據。")}</p>
    </section>
    <section className="about-craft">
      <div className="about-section-index"><span>04</span><p>{t("打磨與用心")}</p></div>
      <div className="about-craft-layout"><div className="about-craft-art"><img src="/assets/forest-ui/marketing/otter-reader-v5.webp" alt={t("小森安靜地翻開一本書")} loading="lazy" /></div>
      <div className="about-craft-copy"><h2>{t("一片葉子的形狀，也值得反覆推敲")}</h2><p>{t("我們是 3 個人的團隊，會為了一個小小的畫面反覆回頭看：小森的眼睛要朝向孩子，還是朝向老師？帽子和書包能不能在手機的小畫面裡看清楚？Logo 裡的一片葉子，放進按鈕後還能不能被辨認？")}</p><p>{t("水獺保持同一種平面繪本筆觸，靠姿態與服裝出現在不同場景；課堂操作則維持「選學生、選原因」兩步。")}</p><div className="about-craft-details"><span>{t("一套一致的繪本色彩與角色")}</span><span>{t("讓課堂操作保持輕巧的流程")}</span><span>{t("學生不用帳號，名單建好就能開始")}</span></div></div></div>
    </section>
    <section className="about-final"><p className="eyebrow">{t("免費版永久可用")}</p><h2>{t("老師給出的每一次肯定，")}<br />{t("都留下紀錄")}</h2><p>{t("有問題或建議，歡迎寫信給我們。免費版不綁信用卡，學生不用註冊。")}</p><div><a className="button primary" href={teacherHref("/register")}>{t("免費開始使用")}</a><a className="button secondary" href="mailto:support@classmori.com">{t("寫信給我們")}</a></div></section>
    <PublicFooter />
  </main>;
}

export function ContactPage() {
  return <main className="public-page contact-page">
    <PublicHeader />
    <section className="contact-hero">
      <div className="contact-intro"><span className="eyebrow">{t("一起聊聊")}</span><h1>{t("產品問題、學校詢價，")}<br />{t("都可以找我們。")}</h1><p>{t("想了解小森課堂、詢問學校方案，或分享你在教室裡遇到的需求？留下訊息，我們會閱讀並回覆。")}</p>
        <div className="contact-channels">{isDefaultLocale && <a href={LINE_URL} target="_blank" rel="noopener noreferrer">{t("用 LINE 聊聊")} <span aria-hidden="true">↗</span></a>}<a href="mailto:support@classmori.com">{t("寄電子郵件")} <span aria-hidden="true">↗</span></a></div>
        <img src="/assets/forest-ui/marketing/teamwork-v4.webp" alt={t("森林教室裡一起合作的孩子")} loading="lazy" />
      </div>
      <div className="contact-card"><p className="eyebrow">{t("寫訊息給我們")}</p><h2>{t("我們會閱讀每則訊息並回覆")}</h2><ContactForm kind="public" /></div>
    </section>
    <PublicFooter />
  </main>;
}

export type TourData = { snapshot: Snapshot; history: HistoryItem[]; routineDemo: TourRoutineDemo };

/** Mounts its children once the element is within `margin` of the viewport (at once without IntersectionObserver). */
export function useNearViewport<T extends Element>(enabled: boolean, margin = "900px"): [React.RefObject<T | null>, boolean] {
  const ref = useRef<T | null>(null);
  const [near, setNear] = useState(!enabled || typeof IntersectionObserver === "undefined");
  useEffect(() => {
    if (near || !ref.current) return;
    const observer = new IntersectionObserver((entries) => { if (entries.some((entry) => entry.isIntersecting)) setNear(true); }, { rootMargin: margin });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [near, margin]);
  return [ref, near];
}

export function TourModeSection({ tour, error }: { tour?: TourData; error?: string }) {
  // D-259: one classroom only — the shared-pet demo is retired with the mode (old #tour-shared links land here).
  return <section id="tour-individual" className="tour-mode-section tour-mode-section-featured" aria-labelledby="tour-individual-title">
    <div className="tour-mode-heading">
      <h2 id="tour-individual-title">{t("一人養一隻，每個孩子都有自己的夥伴")}</h2>
      <p>{t("每位學生的目前積分推動自己的寵物成長；點選下方孩子、試著加減分，看看他的夥伴點數和模樣怎麼變。")}</p>
      {tour && <div className="tour-facts"><span>{t("{count} 位學生", { count: tour.snapshot.students.length })}</span><span>{t("{count} 個小組", { count: tour.snapshot.groups.length })}</span><span>{t("{count} 筆課堂回饋", { count: tour.history.length })}</span></div>}
    </div>
    {error ? <div className="tour-error"><h3>{t("這間演示教室暫時無法載入")}</h3><p>{error}</p><button className="button secondary" onClick={() => location.reload()}>{t("重新載入")}</button></div>
      : tour ? <ChunkErrorBoundary><React.Suspense fallback={<div className="tour-frame tour-frame-loading" role="status">{t("正在讀取一人一寵演示教室…")}</div>}><InteractiveTour tour={tour} /></React.Suspense></ChunkErrorBoundary>
      : <div className="tour-frame tour-frame-loading" role="status">{t("正在讀取一人一寵演示教室…")}</div>}
  </section>;
}

export function TourPage() {
  const [tour, setTour] = useState<TourData>();
  const [error, setError] = useState<string>();
  const initialScrollDone = useRef(false);
  useEffect(() => {
    let cancelled = false;
    Promise.all([api<TourData>(`/api/public-tour?mode=individual&lang=${locale}`), loadTourSession()])
      // The tour always shows the teacher plan, also when the demo Worker predates the `plan` field.
      .then(([data]) => { if (!cancelled) setTour({ ...data, snapshot: { ...data.snapshot, access: { ...data.snapshot.access, plan: "teacher" } } }); })
      .catch((cause) => { if (!cancelled) setError((cause as Error).message); });
    return () => { cancelled = true; };
  }, []);
  useEffect(() => {
    const wanted = location.hash === "#tour-individual" || location.hash === "#tour-shared" || (!location.hash && new URLSearchParams(location.search).get("mode") === "individual");
    if (initialScrollDone.current || !wanted || !(tour || error)) return;
    initialScrollDone.current = true;
    requestAnimationFrame(() => document.getElementById("tour-individual")?.scrollIntoView({ behavior: "instant" }));
  }, [tour, error]);
  return <div className="tour-page">
    <PublicHeader />
    <section className="tour-intro">
      <SceneFigure scene="tour" className="tour-intro-scene" alt={t("老師在綠黑板旁指向嵌入的示範班投影畫面，學生舉手觀看")} eager />
      <p className="eyebrow">{t("公開示範教室")}</p>
      <h1>{t("老師後台的示範班，可以直接操作")}</h1>
      <p>{t("這是公開示範班級，學生都是虛構代稱。操作只在這個頁面裡，重新整理就回到原樣。")}</p>
      <p>{t("建議這樣玩：在下方教室按「幫助 → 新手導覽」，跟著小森一分鐘走完；也可以自己點學生記分，再逛「課堂工具」「學生管理」和「設定」（示範班不會產生真正的連結）。")}</p>
    </section>
    <TourModeSection tour={tour} error={error} />
    <section className="tour-end"><p className="eyebrow">{t("免費版永久可用")}</p><h2>{t("用自己的班級開始")}</h2><a className="button primary" href={teacherHref("/register")}>{t("免費開始使用")}</a><a className="button secondary" href={lp("/pricing")}>{t("查看方案與價格")}</a></section>
    <section className="tour-help" aria-labelledby="tour-help-title">
      <div className="tour-help-heading">
        <div><h2 id="tour-help-title"><ForestIcon name="storybook" />{t("想再了解怎麼用？")}</h2><p>{t("記分、作業與每日事項、學生專屬連結，都有圖文教程。")}</p></div>
        <a className="button secondary" href={lp("/help")}>{t("瀏覽教程與常見問題")} <span aria-hidden="true">→</span></a>
      </div>
      <nav className="tour-help-links" aria-label={t("推薦使用教程")}>
        <a href={lp("/help/scoring")}><strong>{t("課堂記分")} <span aria-hidden="true">→</span></strong><span>{t("選學生、加分、提醒扣分與撤銷記分。")}</span></a>
        <a href={lp("/help/homework-records")}><strong>{t("作業與每日事項")} <span aria-hidden="true">→</span></strong><span>{t("新班級已有示範項目，點學生就能登記。")}</span></a>
        <a href={lp("/help/student-links")}><strong>{t("學生專屬連結")} <span aria-hidden="true">→</span></strong><span>{t("每位孩子一條私人連結，只看自己的夥伴與點數。")}</span></a>
      </nav>
    </section>
    <PublicFooter />
    <StickyStartBar position="tour_sticky" />
  </div>;
}

// D-190 pricing table: monthly/annual switch, reference prices in the visitor's currency (pricing-fx.ts),
// every paid column fully ticked, and a school section that separates group purchases from custom work.
const CURRENCY_STORAGE_KEY = "classmori_pricing_currency";

function initialCurrency(): string {
  try {
    const saved = localStorage.getItem(CURRENCY_STORAGE_KEY);
    if (saved && FX_RATES[saved]) return saved;
  } catch { /* storage blocked: fall through */ }
  try {
    const match = currencyForTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone ?? "");
    if (match) return match;
  } catch { /* no Intl time zone */ }
  return LOCALE_CURRENCY[locale] ?? "USD";
}

function currencyName(code: string): string {
  try { return new Intl.DisplayNames([intlLocale], { type: "currency" }).of(code) ?? code; } catch { return code; }
}

type PlanColumn = "free" | "teacher" | "plus" | "school";
type Cell = boolean | { no: string } | React.ReactNode;
type Row = [string, Cell, Cell, Cell, Cell];

const PlanCheck = ({ yes }: { yes: boolean }) => yes
  ? <span className="pz-check" role="img" aria-label={t("包含")}><svg viewBox="0 0 20 20" aria-hidden="true"><circle cx="10" cy="10" r="10" /><path d="M5.6 10.4l2.9 2.9 5.9-6.2" /></svg></span>
  : <span className="pz-cross" role="img" aria-label={t("不包含")}><svg viewBox="0 0 20 20" aria-hidden="true"><circle cx="10" cy="10" r="10" /><path d="M6.8 6.8l6.4 6.4M13.2 6.8l-6.4 6.4" /></svg></span>;
const isNote = (cell: Cell): cell is { no: string } => typeof cell === "object" && cell !== null && "no" in cell;
const renderCell = (cell: Cell) => typeof cell === "boolean" ? <PlanCheck yes={cell} />
  : isNote(cell) ? <><PlanCheck yes={false} /><small className="pz-cell-note">{cell.no}</small></>
  : <span className="pz-cell-text">{cell}</span>;

export function PricingPage() {
  const [billingEnabled, setBillingEnabled] = useState(false);
  const [cycle, setCycle] = useState<"annual" | "monthly">("annual");
  const [currency, setCurrency] = useState<string>("USD");
  const [comparePlan, setComparePlan] = useState<PlanColumn>("teacher");
  useEffect(() => {
    void api<{ billingEnabled: boolean }>("/api/public-config")
      .then((config) => setBillingEnabled(config.billingEnabled))
      .catch(() => setBillingEnabled(false));
    setCurrency(initialCurrency());
  }, []);
  const chooseCurrency = (next: string) => {
    setCurrency(next);
    try { localStorage.setItem(CURRENCY_STORAGE_KEY, next); } catch { /* not remembered */ }
  };
  const currencyOptions = useMemo(() => Object.keys(FX_RATES).map((code) => ({ code, name: currencyName(code) })), []);
  const local = (usd: number) => currency === "USD" ? null : formatLocalPrice(usd, currency, intlLocale);
  const approx = (usd: number, suffix?: string) => {
    const text = local(usd);
    return text ? <p className="pz-local">≈ {text}{suffix}</p> : null;
  };
  const prices = priceVars();
  const plus = plusVars();
  const freeLimits = { classes: FREE.classes, students: FREE.students };
  const teacherLimits = { classes: TEACHER.classes, students: TEACHER.students };
  const plusLimits = { classes: PLUS.classes, students: PLUS.students };
  const savePercent = Math.round((1 - PRICE.annual / (PRICE.monthly * 12)) * 100);
  const perMonth = (amount: number) => formatPrice(Math.round((amount / 12) * 100) / 100);
  const annual = cycle === "annual";

  const freeToolNote = t("可用免登入的免費工具（不接班級）");
  const devNote = t("依需求開發");
  const species = t("{count} 種", { count: TEACHER.species });
  const themes = t("4 套配色、4 個舞台");
  // Each row: feature, then free / teacher / plus / school. true ✓, false ✗, a string is shown as is,
  // and { no } is ✗ with a short note (the free public tool that does the same job).
  const all = (feature: string): Row => [feature, true, true, true, true];
  const paid = (feature: string, note?: string): Row => [feature, note ? { no: note } : false, true, true, true];
  const groups: Array<{ title: string; rows: Row[] }> = [
    { title: t("價格與額度"), rows: [
      [t("費用"), formatPrice(0),
        annual ? <><b>{t("首年 {firstYear}", prices)}</b><br />{t("之後 {annual}／年", prices)}</> : t("{monthly}／月", prices),
        annual ? <><b>{t("首年 {plusFirstYear}", plus)}</b><br />{t("之後 {plusAnnual}／年", plus)}</> : t("{plusMonthly}／月", plus), t("詢價")],
      [t("使用中的班級"), String(FREE.classes), String(TEACHER.classes), <b>{PLUS.classes}</b>, t("依學校規模")],
      [t("每班學生"), String(FREE.students), String(TEACHER.students), String(PLUS.students), t("依學校規模")],
      [t("一人一寵的寵物種類"), t("{count} 種", { count: FREE.species }), species, species, species],
    ] },
    { title: t("記分與寵物"), rows: [
      all(t("具體正向加分與提醒扣點")), all(t("個人、小組、全班一起加分")), all(t("撤銷記分")),
      all(t("每個孩子一隻會長大的寵物")), all(t("寵物成長到 Lv.30，四種模樣與進化動畫")), all(t("孩子自己挑選夥伴、孵蛋儀式")),
      all(t("積分排行、點數日曆與變化")), all(t("班級成就徽章")),
      paid(t("驚喜獎勵：扭蛋、刮刮卡")),
      [t("課堂配色與寵物舞台"), t("森林配色、森林小徑舞台"), themes, themes, themes],
    ] },
    { title: t("課堂投影與工具"), rows: [
      all(t("電腦投影的課堂畫面")), all(t("抽籤點名（依今日點名排除缺席）")), all(t("倒數計時與碼表")), all(t("隨機分組（可設定不要同組）")),
      all(t("課堂指令板")), all(t("安靜挑戰（麥克風音量）")), all(t("骰子與硬幣、隨機數字")), all(t("隨堂投票")), all(t("鍵盤快捷鍵與課堂捷徑")),
      paid(t("後台課堂遊戲，答對直接加分（九九乘法、比手畫腳、終極密碼）"), freeToolNote),
    ] },
    { title: t("作業與班級日常"), rows: [
      all(t("作業總表：交作業、訂正、加點規則")), all(t("每日事項（潔牙等）與投影")), all(t("今日總表、週／月統計")), all(t("出缺席點名")),
      all(t("值日生輪流表")), all(t("聯絡簿（可加注音與圖片）")), all(t("兌換商店與建議獎勵")), all(t("課堂紀錄與記分歷史")),
    ] },
    { title: t("學生名單"), rows: [
      all(t("Excel／CSV 匯入、直接貼上名單")), all(t("學生照片與座號")), all(t("小組管理")), all(t("歸檔與還原學生")),
    ] },
    { title: t("給孩子"), rows: [
      paid(t("學生專屬連結：孩子用自己的連結或 QR 卡看夥伴、點數與記錄")),
      paid(t("孩子在連結上申請兌換獎勵，老師批准")),
      paid(t("孩子申請時通知老師")),
    ] },
    { title: t("評語、獎狀與座位"), rows: [
      paid(t("學生評語依本學期獎勵預選優點、帶入全班姓名"), freeToolNote),
      paid(t("AI 評語：全班一次產生，每天最多 150 位學生"), t("免費工具用句庫，不限次數")),
      paid(t("全班具名獎狀"), freeToolNote),
      paid(t("按班保存的座位表（同組坐一起）"), freeToolNote),
    ] },
    { title: t("帳號與資料"), rows: [
      all(t("電腦、平板、手機雲端同步")), all(t("九種介面語言")), all(t("深色模式")), all(t("資料匯出與刪除申請")), all(t("免登入的免費課堂工具")),
    ] },
    { title: t("學校與團體"), rows: [
      [t("多位老師統一開通、由管理者分配名額"), false, false, false, true],
      [t("依老師人數提供團體優惠，統一收據"), false, false, false, true],
      [t("多位老師共同記錄同一位學生"), false, false, false, devNote],
      [t("學校專屬功能、報表或系統整合"), false, false, false, devNote],
    ] },
  ];
  const featureCount = groups.slice(1).reduce((sum, group) => sum + group.rows.length, 0);
  const columns: Array<{ id: PlanColumn; name: string }> = [
    { id: "free", name: t("免費版") }, { id: "teacher", name: t("標準版") }, { id: "plus", name: t("進階版") }, { id: "school", name: t("學校方案") },
  ];

  return <main className="pricing-page pz-page public-page">
    <PublicHeader />
    <section className="pz-hero">
      <p className="eyebrow">{t("方案與價格")}</p>
      <h1>{t("免費版永久可用，")}<br />{t("需要更多班級再升級")}</h1>
      <p className="pz-hero-lead">{t("每個方案都有完整的記分、投影與寵物成長。標準版和進階版第一年享優惠價，學校可另外詢價。")}</p>
      <div className="pz-controls">
        <div className="pz-cycle" role="group" aria-label={t("付款週期")}>
          <button type="button" aria-pressed={!annual} className={!annual ? "active" : ""} onClick={() => setCycle("monthly")}>{t("月付")}</button>
          <button type="button" aria-pressed={annual} className={annual ? "active" : ""} onClick={() => setCycle("annual")}>{t("年付")}<span className="pz-save">{t("省 {percent}%", { percent: savePercent })}</span></button>
        </div>
        <label className="pz-currency">
          <span>{t("參考幣別")}</span>
          <select value={currency} onChange={(event) => chooseCurrency(event.target.value)}>
            {currencyOptions.map(({ code, name }) => <option key={code} value={code}>{code === name ? code : `${code} · ${name}`}</option>)}
          </select>
        </label>
      </div>
    </section>

    <section className="pz-plans" aria-label={t("方案")}>
      <article className="pz-card" aria-label={t("免費版")}>
        <div className="pz-card-head"><h2>{t("免費版")}</h2><p>{t("先在一個班試用")}</p></div>
        <div className="pz-price"><div className="pz-amount"><PriceFigure amount={0} /><span>{t("永久")}</span></div><p className="pz-price-note">{t("不限使用期間 · 不需信用卡")}</p></div>
        <a className="button secondary pz-cta" href={teacherHref("/register")}>{t("免費開始使用")}</a>
        <p className="pz-list-title">{t("重點")}</p>
        <ul className="pz-features">
          <li className="strong">{t("{classes} 個使用中班級，每班最多 {students} 位學生", freeLimits)}</li><li>{t("記分、投影與完整寵物成長")}</li><li>{t("{count} 種寵物", { count: FREE.species })}</li><li>{t("作業、點名、抽籤等日常工具")}</li>
        </ul>
      </article>

      <article className="pz-card featured" aria-label={t("標準版")}>
        <span className="pz-ribbon">{t("最受老師歡迎")}</span>
        <div className="pz-card-head"><h2>{t("標準版")}</h2><p>{t("帶好幾個班的老師")}</p></div>
        {annual
          ? <div className="pz-price"><s className="pz-was">{prices.annual}</s><div className="pz-amount"><PriceFigure amount={PRICE.firstYear} /><span>{t("／首年")}</span></div>
            <p className="pz-price-note">{t("第 2 年起 {annual}／年", prices)}</p>{approx(PRICE.firstYear)}
            <p className="pz-price-fine">{t("約 {perMonth}／月 · 一次買 365 天，不自動續訂", { perMonth: perMonth(PRICE.firstYear) })}</p></div>
          : <div className="pz-price"><div className="pz-amount"><PriceFigure amount={PRICE.monthly} /><span>{t("／月")}</span></div>
            <p className="pz-price-note">{t("每月自動續訂，可隨時取消")}</p>{approx(PRICE.monthly, t("／月"))}
            <p className="pz-price-fine">{t("改選年付，首年只要 {firstYear}", prices)}</p></div>}
        {billingEnabled
          ? <a className="button primary pz-cta" href={teacherHref(annual ? "/workspace/billing?plan=annual" : "/workspace/billing?plan=monthly")}>{annual ? t("選擇年付") : t("選擇月付")}</a>
          : <a className="button primary pz-cta" href={teacherHref("/register")}>{t("註冊並試用 30 天")}</a>}
        <p className="pz-list-title">{t("免費版全部，再加上")}</p>
        <ul className="pz-features">
          <li className="strong">{t("{classes} 個使用中班級，每班最多 {students} 位學生", teacherLimits)}</li><li>{t("{count} 種寵物", { count: TEACHER.species })}</li><li>{t("學生專屬連結與兌換申請")}</li><li>{t("課堂遊戲、驚喜獎勵、AI 評語")}</li><li>{t("具名獎狀、座位表、更多配色與舞台")}</li>
        </ul>
      </article>

      <article className="pz-card plus" aria-label={t("進階版")}>
        <span className="pz-ribbon soft">{t("班級最多")}</span>
        <div className="pz-card-head"><h2>{t("進階版")}</h2><p>{t("班級特別多的老師")}</p></div>
        {annual
          ? <div className="pz-price"><s className="pz-was">{plus.plusAnnual}</s><div className="pz-amount"><PriceFigure amount={PRICE.plusFirstYear} /><span>{t("／首年")}</span></div>
            <p className="pz-price-note">{t("第 2 年起 {plusAnnual}／年", plus)}</p>{approx(PRICE.plusFirstYear)}
            <p className="pz-price-fine">{t("約 {perMonth}／月 · 一次買 365 天，不自動續訂", { perMonth: perMonth(PRICE.plusFirstYear) })}</p></div>
          : <div className="pz-price"><div className="pz-amount"><PriceFigure amount={PRICE.plusMonthly} /><span>{t("／月")}</span></div>
            <p className="pz-price-note">{t("每月自動續訂，可隨時取消")}</p>{approx(PRICE.plusMonthly, t("／月"))}
            <p className="pz-price-fine">{t("改選年付，首年只要 {plusFirstYear}", plus)}</p></div>}
        {billingEnabled
          ? <a className="button primary pz-cta" href={teacherHref(annual ? "/workspace/billing?plan=plus" : "/workspace/billing?plan=plus-monthly")}>{annual ? t("選擇年付") : t("選擇月付")}</a>
          : <a className="button primary pz-cta" href={teacherHref("/register")}>{t("註冊並試用 30 天")}</a>}
        <p className="pz-list-title">{t("標準版全部，再加上")}</p>
        <ul className="pz-features">
          <li className="strong">{t("{classes} 個使用中班級，每班最多 {students} 位學生", plusLimits)}</li><li>{t("班級數是標準版的 2 倍")}</li><li>{t("適合同時帶很多班的科任老師")}</li>
        </ul>
      </article>

      <article className="pz-card school" aria-label={t("學校方案")}>
        <div className="pz-card-head"><h2>{t("學校方案")}</h2><p>{t("全校或多位老師一起用")}</p></div>
        <div className="pz-price"><div className="pz-amount quote"><strong>{t("詢價")}</strong></div><p className="pz-price-note">{t("依老師人數與需求報價")}</p></div>
        <a className="button secondary pz-cta" href={schoolQuoteHref()}>{t("聯絡我們詢價")}</a>
        <p className="pz-list-title">{t("進階版全部功能，再加上")}</p>
        <ul className="pz-features">
          <li>{t("多位老師統一開通、由管理者分配名額")}</li><li>{t("依老師人數提供團體優惠，統一收據")}</li><li>{t("多位老師共同記錄同一位學生（依需求開發）")}</li>
        </ul>
      </article>
    </section>
    <p className="pz-fx-note">{currency === "USD"
      ? t("價格以美元（USD）計價與結帳。可在上方切換參考幣別。")
      : t("以美元（USD）結帳；{currency} 金額依 {date} 匯率換算，僅供參考，實際金額以發卡行為準。", { currency, date: formatDate(FX_DATE, { year: "numeric", month: "long", day: "numeric" }) })}</p>

    <section className="pricing-principles pz-principles"><div><ForestIcon name="acorn" /><h3>{t("免費版一直能用")}</h3><p>{t("1 個班、每班 40 位學生，不限使用期間，不需信用卡。")}</p></div><div><ForestIcon name="sunrise" /><h3>{t("先試標準版")}</h3><p>{t("註冊送 30 天標準版試用；結束後自動轉為免費版，不扣款。")}</p></div><div><ForestIcon name="checklist" /><h3>{t("年付不自動續訂")}</h3><p>{t("年付一次買 365 天，不會自動扣款；月付可隨時取消。")}</p></div><div><ForestIcon name="shield" /><h3>{t("買了不會被漲價")}</h3><p>{t("續訂年付按你當初的標價，日後調低則按較低價；首年 {firstYear}，之後每年 {annual}。", prices)}</p></div></section>

    <section className="pz-compare" id="compare">
      <div className="section-heading"><p className="eyebrow">{t("完整功能比較")}</p><h2>{t("{count} 項功能，逐項比較", { count: featureCount })}</h2><p>{t("孩子在投影上看到的記分與寵物成長，每個方案都一樣完整；付費方案多的是班級數，以及幫你省時間的功能。")}</p></div>
      <div className="pz-compare-tabs" role="group" aria-label={t("選擇要比較的方案")}>
        {columns.filter((column) => column.id !== "free").map((column) => <button key={column.id} type="button" aria-pressed={comparePlan === column.id} className={comparePlan === column.id ? "active" : ""} onClick={() => setComparePlan(column.id)}>{column.name}</button>)}
      </div>
      <div className="pz-table-wrap">
        <table className="pz-table" data-plan={comparePlan}>
          <thead><tr><th scope="col"><span className="sr-only">{t("比較項目")}</span></th>{columns.map((column) => <th key={column.id} scope="col" data-col={column.id} className={column.id === "teacher" ? "highlight" : ""}>{column.name}</th>)}</tr></thead>
          {groups.map((group) => <tbody key={group.title}>
            <tr className="pz-group"><th scope="rowgroup" colSpan={5}>{group.title}</th></tr>
            {group.rows.map(([feature, ...cells]) => <tr key={feature}><th scope="row">{feature}</th>{cells.map((cell, index) => <td key={columns[index].id} data-col={columns[index].id} className={columns[index].id === "teacher" ? "highlight" : ""}>{renderCell(cell)}</td>)}</tr>)}
          </tbody>)}
        </table>
      </div>
    </section>

    <section className="pz-school" id="school">
      <div className="pz-school-head"><p className="eyebrow">{t("學校與團體方案")}</p><h2>{t("全校一起用，依需求報價")}</h2><p>{t("下面兩種情況請直接聯絡我們。我們會依老師人數與需求評估，再提供報價。")}</p></div>
      <div className="pz-school-grid">
        <article>
          <ForestIcon name="community" /><h3>{t("多位老師一起購買")}</h3><p className="pz-school-sub">{t("全校、同學年或教學團隊統一採購")}</p>
          <ul className="pz-features"><li>{t("一次為多位老師開通標準版或進階版")}</li><li>{t("依老師人數提供團體優惠")}</li><li>{t("統一收據，配合學校採購與核銷流程")}</li><li>{t("由一位管理者分配與調整老師名額")}</li></ul>
        </article>
        <article>
          <ForestIcon name="settings" /><h3>{t("學校專屬需求")}</h3><p className="pz-school-sub">{t("需要另外開發的功能，評估後報價")}</p>
          <ul className="pz-features"><li>{t("多位老師共同記錄同一位學生：導師、科任老師各自登入，點數與紀錄匯整在同一位學生身上")}</li><li>{t("依學校的獎勵制度客製規則與報表")}</li><li>{t("與校內系統或帳號整合")}</li><li>{t("其他需要另外開發的功能")}</li></ul>
          <p className="pz-school-note">{t("這類功能目前還沒有內建，會依學校需求規劃開發與時程。")}</p>
        </article>
      </div>
      <ol className="pz-steps"><li><b>1</b>{t("填寫詢價表單，說明老師人數與需求")}</li><li><b>2</b>{t("我們評估後回覆方案與報價")}</li><li><b>3</b>{t("確認後開通，或排入開發")}</li></ol>
      <div className="pz-school-actions"><a className="button primary" href={schoolQuoteHref()}>{t("填寫學校詢價")}</a><a className="button secondary" href="mailto:support@classmori.com">support@classmori.com</a></div>
    </section>

    <section className="pricing-faq"><div className="section-heading"><p className="eyebrow">{t("價格常見問題")}</p><h2>{t("每一筆費用，都先說清楚")}</h2></div><div className="faq-grid">
      <details><summary>{t("免費版可以用多久？")}</summary><p>{t("沒有期限。免費版 1 個使用中班級、每班最多 40 位學生；記分、兩種養寵模式與完整成長、投影、課堂紀錄、兌換商店、例行板與常用課堂工具都能用，不需要信用卡。")}</p></details>
      <details><summary>{t("30 天試用結束後會怎樣？")}</summary><p>{t("註冊送的是 30 天標準版試用，不需信用卡。結束後帳號自動轉為免費版，不會扣款，也不會刪除資料；寵物、點數與紀錄都保留。")}</p></details>
      <details><summary>{t("我有 2 個班，或班上有 45 位學生，怎麼辦？")}</summary><p>{t("免費版會保留最早建立的那個使用中班級；如果它超過 40 位學生，歸檔到 40 位以內就能繼續記分。其他班級仍然看得到，但不能記分。你可以封存班級（包括最後一個班）或歸檔學生，自己決定保留哪一班，或升級標準版（5 個班、每班 100 位學生）。")}</p></details>
      <details><summary>{t("標準版多少錢？")}</summary><p>{t("月付 {monthly}／月；年付 {annual}／年。工作空間第一次買年付時，結帳會自動套用首年價 {firstYear}，之後每年 {annual}。", prices)}</p></details>
      <details><summary>{t("以後會漲價嗎？")}</summary><p>{t("買過年付的老師，之後續訂年付都按當初購買時的年付標價（第一次的首年 {firstYear} 是優惠，續訂按 {annual}）；如果日後價格調低，就按較低的價格。調價只適用於新購買的老師。", prices)}</p></details>
      <details><summary>{t("年付會自動續訂嗎？")}</summary><p>{t("不會。年付是一次購買 365 天，不會自動扣款；到期前想繼續用，再自己購買。到期後沒有續購，帳號會轉為免費版。")}</p></details>
      <details><summary>{t("月付可以隨時取消嗎？")}</summary><p>{t("可以。月付每月自動續訂，登入後在「方案與權益」取消即可；已付款的這一期結束前仍可使用標準版，之後轉為免費版。")}</p></details>
      <details><summary>{t("首年優惠要輸入優惠碼嗎？")}</summary><p>{t("不用。工作空間第一次購買年付時，結帳會自動套用首年價；前往付款前會顯示實際金額。")}</p></details>
      <details><summary>{t("可以用新台幣或其他貨幣付款嗎？")}</summary><p>{t("年付可以直接用新台幣付款：在「我的帳號 → 方案與權益」選擇新台幣，用超商代碼或銀行轉帳等台灣常用方式繳費。月付以美元信用卡每月自動扣款。用信用卡以美元付款時，頁面上的其他幣別是依 {date} 匯率換算的參考金額，實際扣款依你的發卡行匯率為準；海外交易手續費由發卡行決定，小森課堂不另外收取。", { date: formatDate(FX_DATE, { year: "numeric", month: "long", day: "numeric" }) })}</p></details>
      <details><summary>{t("免費課堂小工具真的免費嗎？")}</summary><p>{t("是。官網的抽籤、計時器、九九乘法、評語產生器、獎狀、座位表等免費工具，不用登入、不用註冊就能用，也不限次數；免費評語產生器用句庫組合評語，不使用 AI。標準版提供的是接上你的班級名單、點數與寵物的版本，另有 AI 評語。")}</p></details>
      <details><summary>{t("現在可以直接付款嗎？")}</summary><p>{billingEnabled ? t("可以。登入後可在「方案與帳務」選擇方案，前往託管結帳頁；權益經伺服器確認付款後開通。") : t("目前可先註冊，免費試用標準版 30 天；線上結帳開放後，會在「方案與帳務」提供購買入口。")}</p></details>
      <details><summary>{t("試用期間包含哪些功能？")}</summary><p>{t("標準版的全部功能：5 個班、每班 100 位學生、40 種寵物、課堂遊戲加分、驚喜獎勵、評語（含 AI 評語）、學生專屬連結、獎狀、座位表，以及全部配色與舞台。")}</p></details>
      <details><summary>{t("標準版 5 個班不夠用怎麼辦？")}</summary><p>{t("選進階版：{classes} 個使用中班級、每班 {students} 位學生，月付 {plusMonthly}／月，或年付 {plusAnnual}（第一次買進階年付 {plusFirstYear}）。更多請聯絡學校詢價。", plus)}</p></details>
      <details><summary>{t("香港或其他地區可以使用嗎？")}</summary><p>{t("可以。小森課堂統一以美元計價，介面提供繁體中文、簡體中文、英文、西班牙文、法文、葡萄牙文、土耳其文、越南文與印尼文。")}</p></details>
      <details><summary>{t("學校如何統一採購？")}</summary><p>{t("學校或多位老師一起用採個別報價：多位老師一起購買可享團體優惠；需要另外開發的功能（例如多位老師共同記錄同一位學生）會評估後報價。請到聯絡頁選「學校詢價」，或來信 support@classmori.com，附上預計的老師、班級與學生數量。")}</p></details>
      <details><summary>{t("學生需要自己購買嗎？")}</summary><p>{t("不需要。方案由老師或學校購買，學生不需帳號，也不會向學生收費。")}</p></details>
    </div></section>
    <section className="marketing-final"><BrandMark /><p className="eyebrow">{t("方案")}</p><h2>{t("免費版一直都在，標準版可試用 30 天")}</h2><div><a className="button primary" href={teacherHref("/register")}>{t("免費開始使用")}</a><a className="button secondary" href={lp("/about")}>{t("認識我們")}</a></div></section>
    <PublicFooter />
  </main>;
}

export function PublicPage({ kind }: { kind: "pricing" | "privacy" | "terms" }) {
  if (kind === "pricing") return <PricingPage />;
  const content = {
    privacy: {
      title: t("隱私政策"),
      icon: "shield" as ForestIconName,
      body: <PrivacyContent />,
    },
    terms: {
      title: t("服務條款"),
      icon: "scroll" as ForestIconName,
      body: <TermsContent />,
    },
  }[kind];
  return (
    <main className="public-page">
      <PublicHeader />
      <article className="policy-card">
        <div className="policy-heading"><ForestIcon name={content.icon} /><div><p className="eyebrow">{t("小森課堂公開說明")}</p><h1>{content.title}</h1></div></div>
        {content.body}
        <nav className="policy-links" aria-label={t("其他公開說明")}><a href={lp("/about")}>{t("關於我們")}</a><a href={lp("/pricing")}>{t("方案")}</a><a href={lp("/privacy")}>{t("隱私")}</a><a href={lp("/terms")}>{t("使用條款")}</a></nav>
      </article>
      <PublicFooter />
    </main>
  );
}
